import { readonly, ref } from "vue";
import { publicApi } from "@/api/public.api";
import { setAccessToken } from "@/api/http";

export interface CustomerMe {
  idcardHash: string;
  hospId?: string;
  title?: string | null;
  fname: string | null;
  lname: string | null;
  idcardMasked: string;
  idcard?: string | null;
  accessToken?: string;
  birthdate?: string | null;
  gender?: string | null;
  phone: string | null;
  postalcode: string | null;
  addressno: string | null;
  sendaddress: string | null;
  sendaddressid: boolean;
  jobname: string | null;
  diseaseinfo: string | null;
  hn: string | null;
  bloodGroup: string | null;
  allergies: string | null;
  email: string | null;
  emergencyName: string | null;
  emergencyRelation: string | null;
  emergencyPhone: string | null;
  weight: number | null;
  height: number | null;
  /** ISO time the customer last updated weight/height ("current" body data). */
  bodyMeasuredAt?: string | null;
  age: number | null;
  pinSetAt: string | null;
  lastLoginAt: string | null;
  deviceCount: number;
  // Phase 12.12 — replaces `latest` (tinfopatient summary). Visit rows are
  // now created downstream by appointment intake, so the home page surfaces
  // the next upcoming appointment instead of the last visit.
  upcomingAppointment: {
    id: string;
    hospId: string;
    appointmentType: string;
    scheduledAt: string;
    status: string;
    doctorHospId: string | null;
    doctorUsrid: string | null;
  } | null;
}

export interface CustomerProfilePatch {
  title?: string;
  fname?: string;
  lname?: string;
  fnameth?: string;
  lnameth?: string;
  birthdate?: string;
  gender?: string;
  idcard?: string;
  phone?: string;
  postalcode?: string;
  addressno?: string;
  sendaddress?: string;
  sendaddressid?: boolean;
  jobname?: string;
  diseaseinfo?: string;
  bloodGroup?: string;
  allergies?: string;
  wg?: number;
  hg?: number;
  email?: string;
  emergencyName?: string;
  emergencyRelation?: string;
  emergencyPhone?: string;
}

// Module-scoped cache so the 7 sub-pages share one fetch of /public/patient/me.
// Without this, every navigation between Profile/History/Receipt/etc would
// re-hit the BE.
const me = ref<CustomerMe | null>(null);
let inflight: Promise<CustomerMe | null> | null = null;

async function fetchMe(force = false): Promise<CustomerMe | null> {
  if (me.value && !force) return me.value;
  if (inflight && !force) return inflight;
  inflight = publicApi
    .me()
    .then((m) => {
      me.value = m;
      return m;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

async function update(patch: CustomerProfilePatch): Promise<CustomerMe> {
  const updated = await publicApi.updateMe(patch);
  if ((updated as { accessToken?: string }).accessToken) {
    setAccessToken((updated as { accessToken?: string }).accessToken!, "customer");
  }
  me.value = updated as CustomerMe;
  return me.value;
}

export function useCustomerMe() {
  return {
    me: readonly(me),
    load: fetchMe,
    update,
    reset(): void {
      me.value = null;
      inflight = null;
    },
  };
}
